import { useCallback, useEffect, useRef, useState } from 'react';
import type { Map as MapLibreMap } from 'maplibre-gl';

import type { GeoJSONFeatureCollection } from '../types/gis';

export type DrawMode = 'view' | 'edit' | 'draw';

export interface UseMapDrawOptions {
  mapInstance: MapLibreMap | null;
  geojson: GeoJSONFeatureCollection;
  mode: DrawMode;
  onChange?: (next: GeoJSONFeatureCollection) => void;
}

export interface UseMapDrawHandle {
  setMode: (m: DrawMode) => void;
  isDirty: boolean;
  reset: () => void;
}

export function useMapDraw(opts: UseMapDrawOptions): UseMapDrawHandle {
  const drawRef = useRef<any>(null);
  const [isDirty, setIsDirty] = useState(false);

  // Install / uninstall draw control based on mode
  useEffect(() => {
    if (!opts.mapInstance) return;
    if (opts.mode === 'view') {
      // Tear down draw control if present
      if (drawRef.current) {
        try {
          opts.mapInstance.removeControl(drawRef.current);
        } catch {
          /* ignore */
        }
        drawRef.current = null;
      }
      return;
    }

    // Lazy-load mapbox-gl-draw only when entering edit/draw mode
    let cancelled = false;
    (async () => {
      const mod = await import('@mapbox/mapbox-gl-draw');
      if (cancelled || !opts.mapInstance) return;
      const MapboxDraw = (mod as any).default;
      const draw = new MapboxDraw({
        displayControlsDefault: false,
        controls: {
          point: opts.mode === 'draw',
          line_string: opts.mode === 'draw',
          polygon: opts.mode === 'draw',
          trash: opts.mode === 'edit',
        },
      });
      try {
        opts.mapInstance.addControl(draw as any);
        drawRef.current = draw;
        if (opts.geojson) {
          draw.set(opts.geojson as any);
        }
        const onUpdate = () => {
          setIsDirty(true);
          if (opts.onChange) {
            opts.onChange(draw.getAll() as any);
          }
        };
        opts.mapInstance.on('draw.create' as any, onUpdate);
        opts.mapInstance.on('draw.update' as any, onUpdate);
        opts.mapInstance.on('draw.delete' as any, onUpdate);
      } catch (e) {
        console.warn('useMapDraw install failed', e);
      }
    })();

    return () => {
      cancelled = true;
      if (drawRef.current && opts.mapInstance) {
        try {
          opts.mapInstance.removeControl(drawRef.current);
        } catch {
          /* ignore */
        }
        drawRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.mapInstance, opts.mode]);

  const setMode = useCallback((_m: DrawMode) => {
    // Mode is parent-controlled; this is a stable callback that the parent
    // uses to flip its own state — see MapArtifactViewer for usage.
  }, []);

  const reset = useCallback(() => {
    setIsDirty(false);
    if (drawRef.current && opts.geojson) {
      try {
        drawRef.current.set(opts.geojson as any);
      } catch {
        /* ignore */
      }
    }
  }, [opts.geojson]);

  return { setMode, isDirty, reset };
}
